/**
 * What this device can show. Decides the default quality tier and the rendering budgets.
 */

export const hasWebGL2 = () => {
	try {
		const canvas = document.createElement('canvas');
		const gl = canvas.getContext('webgl2');
		if (!gl) return false;
		gl.getExtension('WEBGL_lose_context')?.loseContext();
		return true;
	} catch {
		return false;
	}
};

const isMobileDevice = () =>
	/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
	(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const rendererString = () => {
	try {
		const gl = document.createElement('canvas').getContext('webgl2');
		const info = gl?.getExtension('WEBGL_debug_renderer_info');
		const name = info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : '';
		gl?.getExtension('WEBGL_lose_context')?.loseContext();
		return name || '';
	} catch {
		return '';
	}
};

/**
 * @returns {{
 *   deviceClass: 'desktop'|'laptop'|'tablet'|'phone',
 *   isMobile: boolean,
 *   lodSplatCount: number,
 *   lodRenderScale: number,
 *   maxPixelRatio: number,
 *   pointErrorTarget: number,
 * }}
 */
export const getDeviceProfile = () => {
	const mobile = isMobileDevice();
	if (mobile) {
		const memory = navigator.deviceMemory; // undefined on iOS
		const lowEnd =
			(memory !== undefined && memory < 4) || typeof OffscreenCanvas === 'undefined';
		return lowEnd
			? {
					deviceClass: 'phone',
					isMobile: true,
					lodSplatCount: 600000,
					lodRenderScale: 2.5,
					maxPixelRatio: 1,
					pointErrorTarget: 32,
				}
			: {
					deviceClass: 'tablet',
					isMobile: true,
					lodSplatCount: 1000000,
					lodRenderScale: 2.0,
					maxPixelRatio: 1.5,
					pointErrorTarget: 24,
				};
	}
	const integrated = /Intel|Apple|Iris|UHD/i.test(rendererString());
	return integrated
		? {
				deviceClass: 'laptop',
				isMobile: false,
				lodSplatCount: 1500000,
				lodRenderScale: 1.5,
				maxPixelRatio: 1.5,
				pointErrorTarget: 16,
			}
		: {
				deviceClass: 'desktop',
				isMobile: false,
				lodSplatCount: 2500000,
				lodRenderScale: 1.0,
				maxPixelRatio: 2,
				pointErrorTarget: 8,
			};
};

/**
 * The model to show. A project gets one model, chosen when it is built (`default_tier`); older
 * manifests may list more, then the default wins and any ready one is the fallback.
 */
/** Quality tiers, worst to best: a project can have the quick view and a detailed one side by side. */
export const TIER_RANK = { preview: 0, standard: 1, high: 2 };

export const pickTier = manifest => {
	const tiers = manifest?.tiers ?? {};
	const ready = key => tiers[key]?.status === 'ready' && tiers[key]?.blocks?.length > 0;
	const best = Object.keys(tiers)
		.filter(ready)
		.sort((a, b) => (TIER_RANK[b] ?? -1) - (TIER_RANK[a] ?? -1));
	return best[0] ?? null;
};
